import {AfterContentInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                       from '@angular/forms';
import {
  ApDynformsConfigTextbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {
  ApDynformsConfigComboBox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-combobox';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsValidator}                                          from '../../ap-dynforms/ap-dynforms-validator';
import {ApDynformsComponent}                                          from '../../ap-dynforms/ap-dynforms.component';
import {MachineStore}                                                 from '../../stores/docu/machine.store';
import {DriverStore}                                                  from '../../stores/docu/driver.store';
import {InstrumentStore}            from '../../stores/docu/instrument.store';
import {FormStore, IEntryComponent} from '../../stores/layout/form.store';
import {LoginStore}                 from '../../stores/login/login.store';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynformsConfigCheckbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {ApDateService}                                                from '../../ap-core/services/ap-date-service';
import {map}                                                          from 'rxjs/operators';
import {nameOf}                                                       from '../../ap-core/utils/ap-name-of';
import {MachineEditFormData}                                          from './types/ap-machines-edit.types';
import {
  ResourcesDataService
}                                                                     from '../../services/data/resources/resources-data.service';
import {ObjectFactory}                                                from 'ts-tooling';
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IDriver = Data.DocuContext.Driver.IDriver;
import IMachineType = Data.BaseData.IMachineType;

/**
 * Component for editing or creating a machine.
 */
@Component({
    selector: 'ap-machines-edit',
    template: `
    <ap-dynforms [fieldsets]="formConfig" [loading$]="loading$">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_machines_edit_cancel"
                type="button"
                kendoButton class="ap-form-button-left"
                (apSingleClick)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_machines_edit_save"
                type="button"
                kendoButton [themeColor]="'primary'" class="ap-form-button-right"
                [disabled]="(isSaveDisabled$ | async)"
                (apSingleClick)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`,
    standalone: false
})
export class ApMachinesEditComponent implements OnInit, AfterContentInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');

  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  @Input() public data: IMachine | undefined;
  public formConfig: ApDynformsConfigFieldset[] = [];
  public isNewMode = false;
  public loading$: Observable<boolean> = of(true);
  public isSaveDisabled$ = new BehaviorSubject<boolean>(true);

  private _formData: MachineEditFormData | undefined;
  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private loginStore: LoginStore,
              private driversStore: DriverStore,
              private machinesStore: MachineStore,
              private instrumentStore: InstrumentStore,
              private dateService: ApDateService,
              private resourcesDataService: ResourcesDataService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.driversStore.Loading$,
      this.machinesStore.Loading$,
      this.machinesStore.Saving$
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.isNewMode = !this.data;
    this._formData = {
      Kind: this.data?.Kind,
      Name: this.data?.Name,
      Sign: this.data?.Sign,
      DefaultInstrument: this.data?.DefaultInstrument?.Id,
      DefaultDriver: this.data?.DefaultDriver?.Id,
      IsActive: this.isNewMode ? true : this.resourcesDataService.isResourceActive(this.data)
    };
    this.Caption$.next(this.isNewMode ? 'Settings__Lbl_Device_Create' : 'Settings__Lbl_Device_Edit');
    this.formBuilder();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.dynForm.FormValues$.subscribe(() => {
        this.dynForm.form?.updateValueAndValidity();
        const formValues = this.dynForm.form?.getRawValue() as MachineEditFormData;
        const isSaveAvailable = JSON.stringify(formValues) !== JSON.stringify(this._formData)
          && this.dynForm.form?.valid;
        this.isSaveDisabled$.next(!isSaveAvailable);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(d => d?.unsubscribe());
  }

  /**
   * Submitting the changes.
   * Either creating a new object or updating the current.
   */
  public onSubmitClick(): void {
    const formData = this.dynForm.form.getRawValue() as MachineEditFormData;
    if (!formData) {
      return;
    }
    const machine = ObjectFactory.Copy(this.data) ?? ({} as IMachine);
    machine.FarmId = this.loginStore.SelectedFarmId;
    machine.Name = formData.Name;
    machine.Sign = formData.Sign;
    machine.Kind = formData.Kind;
    machine.DefaultInstrument = this.instrumentStore.Instruments.find(x => x.Id === formData?.DefaultInstrument);
    machine.DefaultDriver = this.driversStore.Drivers.find(ins => ins.Id === formData?.DefaultDriver);
    machine.IsDeleted = !formData.IsActive;
    machine.DeletedAt = formData.IsActive ? undefined : this.dateService.getUtc();
    machine.DeletedBy = formData.IsActive ? undefined : this.loginStore.User.Id;
    if (this.isNewMode) {
      this.machinesStore.createMachine(machine);
    } else {
      this.machinesStore.updateMachine(machine);
    }
    this.formStore.closeForm();
  }

  /**
   * Cancel a clicked eventHandler - closes form.
   */
  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  /**
   * binding of validators
   */
  private formBuilder(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'machines',
        config: [
          new ApDynformsConfigComboBox({
            key: nameOf<MachineEditFormData>('Kind'),
            label: 'Docu_Ini__Type',
            value: this._formData.Kind,
            valueField: nameOf<IMachineType>('Id'),
            textField: nameOf<IMachineType>('Description'),
            options: this.machinesStore.MachineTypes$,
            valuePrimitive: true,
            translate: true,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<MachineEditFormData>('Name'),
            label: 'Docu_Ini__Name',
            value: this._formData.Name,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<MachineEditFormData>('Sign'),
            label: 'Docu_Ini__NumberPlate',
            value: this._formData.Sign,
            validators: [
              new ApDynformsValidator({
                validator: Validators.maxLength(20),
                errorKey: 'Global_Not_More_Than_20_Characters'
              }),
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<MachineEditFormData>('DefaultInstrument'),
            label: 'Docu_Ini__Tool_Default',
            value: this._formData.DefaultInstrument,
            valueField: nameOf<IInstrument>('Id'),
            textField: nameOf<IInstrument>('Name'),
            options: this.instrumentStore.Instruments$.pipe(
              map(x => x.filter(y => this.resourcesDataService.isResourceActive(y)))
            ),
            valuePrimitive: true
          }),
          new ApDynformsConfigComboBox({
            key: nameOf<MachineEditFormData>('DefaultDriver'),
            label: 'Docu_Ini__Motorist_Default',
            value: this._formData.DefaultDriver,
            valueField: nameOf<IDriver>('Id'),
            textField: nameOf<IDriver>('FirstNameFullName'),
            options: this.driversStore.Drivers$.pipe(
              map(x => x.filter(y => this.resourcesDataService.isResourceActive(y)))
            ),
            valuePrimitive: true
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<MachineEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive
          })
        ]
      })
    ];
  }
}
